import { ReactElement, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { useIntl } from 'react-intl';

import { useRouter } from 'next/router';
import { localeToSaleorQuery } from '@utils/regions';

import { Button } from '@components/atoms/Button';
import { ErrorMessage } from '@components/atoms/ErrorMessage';
import { TextField } from '@components/molecules/TextField';

import { SelectedAttributeValueInput } from '@sdk/gqlTypes/generatedTypes';
import { useAddWishlistProductVariantMutation } from '@sdk/gqlTypes/generatedHooks';

// import { RudderAnalytics } from '../../../app/analitics';
import { IFormError } from '@types';
import { commonMessages, validationCommonMsg } from '../../../../intl';

import * as S from './styles';

interface Props {
  onSuccess?: () => void;
  productId: string;
  selectedProductAttributes: SelectedAttributeValueInput[];
  productUrl: string;
  variantId: string;
}

export function SaveProjectForm({
  onSuccess,
  productId,
  selectedProductAttributes,
  productUrl,
  variantId,
}: Props): ReactElement {
  const intl = useIntl();
  const router = useRouter();
  const locale = router.query.locale?.toString();
  const [addWishlistProductVariantMutation, { loading, error, data }] =
    useAddWishlistProductVariantMutation();

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm();

  const handleOnSubmit = (values: any): any => {
    addWishlistProductVariantMutation({
      variables: {
        email: values.email,
        productAttributes: selectedProductAttributes,
        variantId,
        ...localeToSaleorQuery(locale),
      },
    });
    // @analitics
    window?.rudderanalytics?.track('Product Shared', {
      product_id: productId,
      recipient: values.email,
      share_via: 'EMAIL',
      url: productUrl,
      variant: variantId,
    });
  };

  useEffect(() => {
    if (!loading && !error && data) {
      const hasErrors = data?.wishlistAddVariant?.wishlistErrors.length;
      if (!hasErrors && onSuccess) {
        onSuccess();
      }
    }
  }, [loading, error, data]);

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      <span className="form-error">
        <ErrorMessage
          errors={error && [{ field: 'email', message: error.message }]}
        />
      </span>
      <Controller
        name="email"
        control={control}
        rules={{
          pattern: {
            message: intl.formatMessage(validationCommonMsg.invalidEmail),
            value:
              /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
          },
          required: intl.formatMessage(validationCommonMsg.required),
        }}
        render={({ field }) => (
          <TextField
            label={intl.formatMessage(commonMessages.shortEmail)}
            autoComplete="email"
            errors={
              errors.email
                ? [{ message: errors.email.message } as IFormError]
                : undefined
            }
            {...field}
          />
        )}
      />
      <S.SaveButton>
        <Button size="lg" fullWidth type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? intl.formatMessage(commonMessages.loading)
            : intl.formatMessage(commonMessages.saveProject)}
        </Button>
      </S.SaveButton>
    </form>
  );
}
